import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';

import { Autocomplete, Box, Button, CircularProgress, DialogActions, FormLabel, Grid, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography, } from '@mui/material';
import { BusinessOutlined, CheckCircleOutlineOutlined, CommentOutlined, CurrencyRupee, DriveFolderUploadOutlined, InfoOutlined, PersonOutlineOutlined, FolderShared as FolderSharedIcon, } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import FileUploadField from './components/FileUploadField';
import MultiFileUploadField from './components/MultiFileUploadField';
import LoaderText from '../../../../../../../components/loader/LoaderText';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../../../../redux/reducer/snackbarSlice';
import { setModuleRefresh } from '../../../../../../../redux/reducer/registration/RefreshSlice';
import { closeDrawer } from '../../../../../../../redux/reducer/drawerSlice';
import { RootState } from '../../../../../../../redux/store';
import { CreateApplicationValidationSchema, getInitialApplicationData, mapApplicationDataToFormik, } from './components/constant';
import { CreateApplicationFormType, } from '../../../../../../../utils/dto/module/application.types';
import { FetchApplicationDetailService, StoreUpdateNewBeneficiaryApplicationService, } from '../../../../../../../utils/services/registration/application.service';
import { GetHospitalsListService } from '../../../../../../../utils/services/registration/donor-registration.service';
import { ProjectsList } from '../../../../../../../utils/services/project/project';
import { FetchTriPartyMOUListService } from '../../../../../../../utils/services/tri-party-mou/tri.party.mou.service';
import { GetCollabrationAgreementBeneficiariesDetailsService } from '../../../../../../../utils/services/collaboration-agreement/collaboration-agreement.service';

const CreateApplication: React.FC<any> = () => {
    const { drawerData } = useSelector((state: RootState) => state.drawer);

    const UserDetails = useSelector((state: RootState) => state.authUser.userDetails)

    const dispatch = useDispatch();
    const refreshFlag = useSelector((state: RootState) => state.refreshSlice.application);

    const [hospitalOptions, setHospitalOptions] = useState<any[]>(drawerData?.hospitals || []);
    const [projectOptions, setProjectOptions] = useState<any[]>([]);
    const [triPartyMouOptions, setTriPartyMouOptions] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();

    const uuid = searchParams.get("uuid");
    const uid = uuid ? uuid : drawerData?.effectiveUuid;
    // const IsNotHospital: boolean = user_type !== 'hospitals'

    const appendDocumentField = (formData: FormData, key: string, value: File | string | null | undefined) => {
        if (!value) return;
        formData.append(key, value);
    };

    const formik = useFormik<CreateApplicationFormType>({
        initialValues: getInitialApplicationData(UserDetails),
        validationSchema: CreateApplicationValidationSchema,
        onSubmit: async (values) => {
            const formData = new FormData();
            if (drawerData?.isUpdate) formData.append("uuid", drawerData?.beneficiaries_uuid);
            if (!drawerData?.isUpdate || drawerData?.status === "need-attention") formData.append("status", "pre-auth-pending");
            if (drawerData?.isUpdate && drawerData?.status !== "need-attention") {
                formData.append("status", "post-auth-pending");
            }
            if (values.application_date) formData.append("application_date", dayjs(values.application_date).format("YYYY-MM-DD"));
            formData.append("patient_name", values.patient_name);
            formData.append("patient_email", values.patient_email);
            formData.append("patient_contact_no", values.patient_contact);
            formData.append("patient_family_number", values.patient_family_number);
            formData.append("dob", dayjs(values.patient_date_of_birth).format("YYYY-MM-DD"));
            formData.append("village_name", values.village_name);
            formData.append("pincode", values.pin_code);
            if (values.aadhar_no) formData.append("aadhar_no", values.aadhar_no);

            formData.append("annual_income", values.annual_income);
            formData.append("hospital_id", values.hospital_id);
            formData.append("doctor_team_number", values.doctor_team_number);
            formData.append("additional_amount", values.additional_Amount.toString());
            formData.append("estimated_medical_cost", values.estimated_medical_cost.toString());
            formData.append("approved_medical_cost", values.approved_medical_cost.toString());
            formData.append("medical_comment", values.medical_comment);
            appendDocumentField(formData, "hospital_estimate", values.hospital_estimate);

            formData.append("subject", values.subject);
            formData.append("cm_remarks", values.cm_remarks);

            formData.append("type", drawerData?.type);
            formData.append("tri_party_uuid", drawerData?.isUpdate ? values.tri_party_uuid : uid);
            appendDocumentField(formData, "income_certificate", values.income_certificate);
            appendDocumentField(formData, "adhar_card", values.adhar_card);
            appendDocumentField(formData, "ration_card", values.ration_card);
            appendDocumentField(formData, "other_documents", values.other_documents);

            if (values.fund_dignosis_report_file instanceof File) {
                formData.append("fund_dignosis_report_file", values.fund_dignosis_report_file);
            }
            if (values.patient_photo instanceof File) {
                formData.append("patient_photo", values.patient_photo);
            }
            if (Array.isArray(values.supportive_document)) {
                values.supportive_document.forEach((file) => {
                    if (file instanceof File) {
                        formData.append("supportive_document", file);
                    }
                });
            }
            setIsLoading(true)
            const { code, message } = await StoreUpdateNewBeneficiaryApplicationService(formData, drawerData?.isUpdate);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || "New Application Created Successfully" }));
                dispatch(setModuleRefresh({ moduleName: 'application', refresh: !refreshFlag }));
                if (drawerData?.isUpdate) {
                    dispatch(setModuleRefresh({ moduleName: 'user_details', refresh: true }));
                }
                dispatch(closeDrawer());
            }
            else {
                dispatch(showSnackbar({ type: "error", message: message || "Something went wrong please try again" }));
            }
            setIsLoading(false)
        },
    });
    const { setValues } = formik;
    const drawerUuid = drawerData?.uuid;
    const drawerBeneficiariesUuid = drawerData?.beneficiaries_uuid;
    const drawerIsUpdate = drawerData?.isUpdate;
    const drawerType = drawerData?.type;

    useEffect(() => {
        const fetchApplicationDetails = async () => {
            if (drawerUuid || drawerIsUpdate) {
                // For mou-tri-party (Request to CMRF flow) use project/beneficiaries/details
                const isMouType = drawerType === 'mou-tri-party';
                const fetchFn = isMouType
                    ? GetCollabrationAgreementBeneficiariesDetailsService
                    : FetchApplicationDetailService;
                const { code, data } = await fetchFn({ uuid: drawerBeneficiariesUuid });
                if (code === 200 && data) {
                    setValues(mapApplicationDataToFormik(data));
                }
            }
        };

        if (drawerUuid || drawerIsUpdate) {
            fetchApplicationDetails();
        }
    }, [drawerBeneficiariesUuid, drawerIsUpdate, drawerType, drawerUuid, setValues]);

    const fetchHospitalListService = useCallback(async () => {
        setLoading(true);
        const response = await GetHospitalsListService({});

        if (response && response.code === 200 && response.data?.hospitals) {
            setHospitalOptions(response.data.hospitals);
        } else {
            setHospitalOptions(drawerData?.hospitals || []);
        }
        setLoading(false);
    }, [drawerData?.hospitals]);

    const fetchProjectListService = async () => {
        setLoading(true);
        const payload = {};
        const { code, data } = await ProjectsList(payload);
        if (code === 200 && data?.data) {
            setProjectOptions(data.data);
        } else {
            setProjectOptions([]);
        }
        setLoading(false);
    };


    const fetchTriPartyMOUListService = async () => {
        setLoading(true);
        const payload = { limit: 100, status: 'approved' };
        const { code, data } = await FetchTriPartyMOUListService(payload);
        if (code === 200 && data?.data) {
            setTriPartyMouOptions(data.data);
        } else {
            setTriPartyMouOptions([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (drawerData?.type === 'project') {
            fetchProjectListService();
        } else {
            fetchTriPartyMOUListService()
        }
        fetchHospitalListService();
    }, [drawerData?.type, fetchHospitalListService]);

    return (
        <FormikProvider value={formik}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box component="div" sx={{ backgroundColor: 'background.default', height: '100%', }}>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 59px)', maxHeight: 'calc(100vh - 59px)' }}>
                            <Box sx={{ flex: '1 1', overflowY: 'scroll', p: 2, px: 2 }}>

                                <Paper sx={{ boxShadow: 4, p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: '15px' }}>
                                        <PersonOutlineOutlined fontSize='small' /><Typography variant='h5' fontWeight={600}> Beneficiary Details</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Patient’s Full Name / रुग्णाचे संपूर्ण नाव : <span style={{ color: 'red' }}>*</span> </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Patient Name"
                                                name="patient_name"
                                                value={formik.values.patient_name}
                                                onChange={(e) => { formik.setFieldValue('patient_name', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.patient_name && Boolean(formik.errors.patient_name)}
                                                helperText={formik.touched.patient_name && formik.errors.patient_name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Aadhar Number / आधार क्रमांक : <span style={{ color: 'red' }}>*</span> </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Aadhar Number"
                                                name="aadhar_no"
                                                value={formik.values.aadhar_no}
                                                onChange={(e) => { formik.setFieldValue('aadhar_no', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.aadhar_no && Boolean(formik.errors.aadhar_no)}
                                                helperText={formik.touched.aadhar_no && formik.errors.aadhar_no}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> E-Mail Id / ई-मेल आयडी :  </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Patient Email"
                                                name="patient_email"
                                                value={formik.values.patient_email}
                                                onChange={(e) => { formik.setFieldValue('patient_email', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.patient_email && Boolean(formik.errors.patient_email)}
                                                helperText={formik.touched.patient_email && formik.errors.patient_email}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Patient’s Mob No. / रुग्णाचे मो. नंबर : <span style={{ color: 'red' }}>*</span> </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Patient Mob Number"
                                                name="patient_contact"
                                                value={formik.values.patient_contact}
                                                inputProps={{ maxLength: 10, pattern: "[0-9]*", inputMode: "numeric", }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('patient_contact', value) } }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.patient_contact && Boolean(formik.errors.patient_contact)}
                                                helperText={formik.touched.patient_contact && formik.errors.patient_contact}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>Patient’s  Date of Birth / रुग्णाचे जन्म तारीख :<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <DatePicker
                                                maxDate={dayjs(new Date())}
                                                value={formik.values.patient_date_of_birth ? dayjs(formik.values.patient_date_of_birth) : null}
                                                onChange={(value) =>
                                                    formik.setFieldValue("patient_date_of_birth", value ? value.format("YYYY-MM-DD") : "")
                                                }
                                                sx={{ ".MuiButtonBase-root": { marginRight: 0 } }}
                                                format="DD-MM-YYYY"
                                                slotProps={{
                                                    textField: {
                                                        disabled: true,
                                                        placeholder: "Select Date of Birth",
                                                        fullWidth: true,
                                                        size: "small",
                                                        inputProps: { style: { textOverflow: "ellipsis", WebkitTextFillColor: "black", }, },
                                                        error: formik.touched.patient_date_of_birth && Boolean(formik.errors.patient_date_of_birth),
                                                        helperText: formik.touched.patient_date_of_birth && typeof formik.errors.patient_date_of_birth === "string" ? formik.errors.patient_date_of_birth : "",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>Patient's Relatives' Mob / रुग्णाच्या नातेवाईकांचा मो. नंबर :<span style={{ color: 'red' }}>*</span> </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Patient Mobile Number"
                                                name="patient_family_number"
                                                inputProps={{ maxLength: 10, pattern: "[0-9]*", inputMode: "numeric", }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('patient_family_number', value) } }}
                                                value={formik.values.patient_family_number}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.patient_family_number && Boolean(formik.errors.patient_family_number)}
                                                helperText={formik.touched.patient_family_number && formik.errors.patient_family_number}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>  Village / गाव :</FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Village Name"
                                                name="village_name"
                                                value={formik.values.village_name}
                                                onChange={(e) => { formik.setFieldValue('village_name', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.village_name && Boolean(formik.errors.village_name)}
                                                helperText={formik.touched.village_name && formik.errors.village_name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>  Pin Code / पिनकोड : <span style={{ color: 'red' }}>*</span></FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Pin Code"
                                                name="pin_code"
                                                value={formik.values.pin_code}
                                                inputProps={{ maxLength: 6, pattern: "[0-9]*", inputMode: "numeric", }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('pin_code', value); } }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.pin_code && Boolean(formik.errors.pin_code)}
                                                helperText={formik.touched.pin_code && formik.errors.pin_code}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Annual Income / वार्षिक उत्पन्न : </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Annual Income"
                                                name="annual_income"
                                                inputProps={{ maxLength: 20, pattern: "[0-9]*", inputMode: "numeric", style: { textAlign: 'right' }, }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('annual_income', value) } }}
                                                value={formik.values.annual_income}
                                                onBlur={formik.handleBlur}
                                                InputProps={{
                                                    endAdornment: (<InputAdornment position="start"> <CurrencyRupee fontSize="small" /> </InputAdornment>),
                                                }}
                                                error={formik.touched.annual_income && Boolean(formik.errors.annual_income)}
                                                helperText={formik.touched.annual_income && formik.errors.annual_income}
                                            />
                                        </Grid>

                                    </Grid>
                                </Paper>
                                <Paper sx={{ boxShadow: 4, p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: '15px' }}>
                                        <BusinessOutlined fontSize='small' /><Typography variant='h5' fontWeight={600}> Hospital Details</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Select Hospital  <span style={{ color: 'red' }}>*</span> </FormLabel>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={hospitalOptions}
                                                loading={loading}
                                                getOptionLabel={(option) => option.name || option.hospital_name || ""}
                                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                                onChange={(event, value) => formik.setFieldValue("hospital_id", value ? String(value.id) : "")}
                                                value={
                                                    hospitalOptions.find((h) => String(h.id) === String(formik.values.hospital_id)) || null
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        name="hospital_id"
                                                        placeholder="Select Hospital"
                                                        error={formik.touched.hospital_id && Boolean(formik.errors.hospital_id)}
                                                        helperText={formik.touched.hospital_id && formik.errors.hospital_id}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {loading ? <CircularProgress size={20} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Doctor Team Mob No. / डॉक्टर टीम  मो. नंबर : <span style={{ color: 'red' }}>*</span></FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Doctor Team Number"
                                                name="doctor_team_number"
                                                value={formik.values.doctor_team_number}
                                                inputProps={{ maxLength: 10, pattern: "[0-9]*", inputMode: "numeric", }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('doctor_team_number', e.target.value) } }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.doctor_team_number && Boolean(formik.errors.doctor_team_number)}
                                                helperText={formik.touched.doctor_team_number && formik.errors.doctor_team_number}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>Estimated Medical Expenses / अंदाजे वैद्यकीय खर्च :</FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Estimated Medical Expenses"
                                                name="estimated_medical_cost"
                                                value={formik.values.estimated_medical_cost}
                                                inputProps={{ maxLength: 20, pattern: "[0-9]*", inputMode: "numeric", style: { textAlign: 'right' }, }}
                                                onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('estimated_medical_cost', value) } }}
                                                onBlur={formik.handleBlur}
                                                InputProps={{
                                                    endAdornment: (<InputAdornment position="start"> <CurrencyRupee fontSize="small" /> </InputAdornment>),
                                                }}
                                                error={formik.touched.estimated_medical_cost && Boolean(formik.errors.estimated_medical_cost)}
                                                helperText={formik.touched.estimated_medical_cost && formik.errors.estimated_medical_cost}
                                            />
                                        </Grid>
                                        {drawerIsUpdate && <>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormLabel>Amount Approved By Doctor/डॉक्टरांनी मंजूर केलेली रक्कम:</FormLabel>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Enter Approved Amount"
                                                    name="approved_medical_cost"
                                                    value={formik.values.approved_medical_cost}
                                                    inputProps={{ maxLength: 20, pattern: "[0-9]*", inputMode: "numeric", style: { textAlign: 'right' }, }}
                                                    onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('approved_medical_cost', value) } }}
                                                    onBlur={formik.handleBlur}
                                                    InputProps={{
                                                        endAdornment: (<InputAdornment position="start"> <CurrencyRupee fontSize="small" /> </InputAdornment>),
                                                    }}
                                                    error={formik.touched.approved_medical_cost && Boolean(formik.errors.approved_medical_cost)}
                                                    helperText={formik.touched.approved_medical_cost && formik.errors.approved_medical_cost}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormLabel>Additional Amount (if) / अतिरिक्त रक्कम (असेल तर) :</FormLabel>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Enter Additional Amount"
                                                    name="additional_Amount"
                                                    value={formik.values.additional_Amount}
                                                    inputProps={{ maxLength: 20, pattern: "[0-9]*", inputMode: "numeric", style: { textAlign: 'right' }, }}
                                                    onChange={(e) => { const value = e.target.value; if (/^\d*$/.test(value)) { formik.setFieldValue('additional_Amount', value) } }}
                                                    onBlur={formik.handleBlur}
                                                    InputProps={{
                                                        endAdornment: (<InputAdornment position="start"> <CurrencyRupee fontSize="small" /> </InputAdornment>),
                                                    }}
                                                    error={formik.touched.additional_Amount && Boolean(formik.errors.additional_Amount)}
                                                    helperText={formik.touched.additional_Amount && formik.errors.additional_Amount}
                                                />
                                            </Grid>
                                        </>}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel> Diagnosis Remarks / निदान विषयक टिप्पणी : </FormLabel>
                                            <TextField
                                                fullWidth
                                                multiline
                                                size="small"
                                                placeholder="Enter Diagnosis Remarks"
                                                name="medical_comment"
                                                value={formik.values.medical_comment}
                                                onChange={(e) => { formik.setFieldValue('medical_comment', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.medical_comment && Boolean(formik.errors.medical_comment)}
                                                helperText={formik.touched.medical_comment && formik.errors.medical_comment}
                                            />
                                        </Grid>
                                        {drawerIsUpdate && (
                                            <Grid item xs={12} sm={4} md={3}>
                                                <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    Hospital Estimate / हॉस्पिटल अंदाज:
                                                    <Tooltip placement='top' title="Upload Hospital Estimate (up to 5 MB PDF) / ग्णालयाचा अंदाज अपलोड करा (५ एमबी पर्यंत पीडीएफ):" arrow>
                                                        <IconButton color='info' size="small" sx={{ p: 0 }}>
                                                            <InfoOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </FormLabel>
                                                <FileUploadField name="hospital_estimate" label="Upload PDF" accept="application/pdf" />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                                <Paper sx={{ boxShadow: 4, p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: '15px' }}>
                                        <DriveFolderUploadOutlined fontSize='small' /><Typography variant='h5' fontWeight={600}>Documents</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel>
                                                Income Certificate :
                                                <Tooltip placement='top' title="Upload Income Certificate (up to 5 MB PDF) / उत्पन्नाचा दाखला अपलोड करा (५ एमबी पर्यंत पीडीएफ)" arrow>
                                                    <IconButton color='info' size="small" sx={{ ml: 0.5, padding: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <FileUploadField name="income_certificate" label="Upload PDF" accept="application/pdf" />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Aadhar Card  :<span style={{ color: 'red' }}>*</span>
                                                <Tooltip
                                                    placement="top"
                                                    title="Upload Aadhar Card (Up to 5 MB PDF) / आधार कार्ड अपलोड करा (५ एमबी पर्यंत पीडीएफ)"
                                                    arrow
                                                >
                                                    <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <FileUploadField name="adhar_card" label="Choose File" accept="application/pdf" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Ration Card : <span style={{ color: 'red' }}>*</span>
                                                <Tooltip
                                                    placement="top"
                                                    title="Please Upload Ration Card (Front, Back, Both Sides PDF - Maharashtra Only) / कृपया रेशन कार्डची पुढची मागची दोन्ही बाजूंची पीडीएफ अपलोड करा (फ महाराष्ट्रा साठी)"
                                                    arrow
                                                >
                                                    <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <FileUploadField name="ration_card" label="Choose File" accept="application/pdf" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Other Documents / Hospital Documents :
                                                <Tooltip
                                                    placement="top"
                                                    title=" Please Upload Other Documents (Up to 5 MB PDF) / कृपया इतर कागदपत्रे अपलोड करा (५ एमबी पर्यंत पीडीएफ)"
                                                    arrow
                                                >
                                                    <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <FileUploadField name="other_documents" label="Choose File" accept="application/pdf" />
                                        </Grid>
                                        {drawerIsUpdate && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    Fund / Diagnosis Report :
                                                    <Tooltip
                                                        placement="top"
                                                        title="Upload Fund / Diagnosis Report (Up to 5 MB PDF) / निधी / निदान अहवाल अपलोड करा (५ एमबी पर्यंत पीडीएफ)"
                                                        arrow
                                                    >
                                                        <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                            <InfoOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </FormLabel>
                                                <FileUploadField name="fund_dignosis_report_file" label="Choose File" accept="application/pdf" />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Patient Photo :
                                                <Tooltip
                                                    placement="top"
                                                    title="Upload Patient Photo (Up to 5 MB Image) / रुग्णाचा फोटो अपलोड करा (५ एमबी पर्यंत प्रतिमा)"
                                                    arrow
                                                >
                                                    <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <FileUploadField name="patient_photo" label="Choose Image" accept="image/*" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Supportive Documents  :
                                                <Tooltip
                                                    placement="top"
                                                    title="Upload Supportive Documents ( PDFs, each up to 5 MB) / सहाय्यक कागदपत्रे अपलोड करा (अनेक पीडीएफ, प्रत्येकी ५ एमबी पर्यंत)"
                                                    arrow
                                                >
                                                    <IconButton color="info" size="small" sx={{ p: 0 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <MultiFileUploadField name="supportive_document" label="Choose Files" accept="application/pdf" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                                <Paper sx={{ boxShadow: 4, p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: '15px' }}>
                                        <CommentOutlined fontSize='small' /><Typography variant='h5' fontWeight={600}>Comments / Remarks</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={6}>
                                            <FormLabel>CMs Remarks(if) :</FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Letter Ref No"
                                                name="cm_remarks"
                                                multiline
                                                minRows={2}
                                                value={formik.values.cm_remarks}
                                                onChange={(e) => { formik.setFieldValue('cm_remarks', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.cm_remarks && Boolean(formik.errors.cm_remarks)}
                                                helperText={formik.touched.cm_remarks && formik.errors.cm_remarks}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={6}>
                                            <FormLabel>Subject / विषय : </FormLabel>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter Letter Ref No"
                                                name="subject"
                                                multiline
                                                minRows={2}
                                                value={formik.values.subject}
                                                onChange={(e) => { formik.setFieldValue('subject', e.target.value); }}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.subject && Boolean(formik.errors.subject)}
                                                helperText={formik.touched.subject && formik.errors.subject}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>


                            </Box>
                            {isLoading && <LoaderText />}
                            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', py: 1, px: 2, backgroundColor: '#f5f5f5' }}>
                                <Button type="submit" size='large' variant="contained" disabled={isLoading} color="success" endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineOutlined />} >
                                    {isLoading ? drawerData?.isUpdate ? 'Updating...' : 'Submitting...' : drawerData?.isUpdate ? 'Update Application' : 'Submit Application'}
                                </Button>
                            </DialogActions>
                        </Box>
                    </form>
                </Box>
            </LocalizationProvider>
        </FormikProvider>
    );
};

export default CreateApplication;
